"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/shadcn/dialog";
import { inputsToNodeOptions } from "@/components/Form/ComplexMarkdownField/inputsToNodeOptions";
import { VariablesSidebar } from "@/components/Form/ComplexMarkdownField/ComplexMarkdownDialog";
import type { EditorInput } from "@/components/Form/ComplexMarkdownField/types";
import type { VariableRefSyntax } from "@/components/SpotflowV2/Sidebar/variableRefs";
import {
  CodeEditorSurface,
  type CodeEditorHandle,
  type CodeEditorLanguage,
} from "./CodeEditor";

interface CodeEditorDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue: string;
  onCommit: (value: string) => void;
  inputs: EditorInput[];
  label?: string;
  placeholder?: string;
  isDisabled?: boolean;
  language?: CodeEditorLanguage;
  nodeId?: string;
  variableBindings?: Record<string, string | undefined>;
  refSyntax?: VariableRefSyntax;
}

// Edits accumulate in a local draft and commit to the form once, on close.
export function CodeEditorDialog({
  isOpen,
  onOpenChange,
  initialValue,
  onCommit,
  inputs,
  label,
  placeholder,
  isDisabled,
  language,
  nodeId,
  variableBindings,
  refSyntax,
}: CodeEditorDialogProps) {
  const [draft, setDraft] = useState(initialValue);
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const editorRef = useRef<CodeEditorHandle>(null);
  const variableOptions = useMemo(() => inputsToNodeOptions(inputs), [inputs]);

  useEffect(() => {
    if (isOpen) setDraft(initialValue);
  }, [isOpen, initialValue]);

  const handleOpenChange = (open: boolean) => {
    if (!open && !isDisabled) {
      onCommit(draftRef.current);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent size="6xl" className="flex flex-col min-h-[80%] p-0 gap-0">
        <DialogHeader className="shrink-0 px-6 py-4 border-b">
          <DialogTitle>{label ?? "Edit"}</DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 flex-1">
          {variableOptions.some((n) => n.outputs.length > 0) && (
            <VariablesSidebar
              options={variableOptions}
              onInsert={(id) => editorRef.current?.insertVariable(id)}
            />
          )}
          <div className="flex min-h-0 flex-1 flex-col p-4">
            {isOpen && (
              <CodeEditorSurface
                ref={editorRef}
                value={draft}
                onChange={setDraft}
                placeholder={placeholder}
                isDisabled={isDisabled}
                inputs={inputs}
                language={language}
                nodeId={nodeId}
                variableBindings={variableBindings}
                refSyntax={refSyntax}
                fillHeight
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
